import React, { Component } from "react";
import PropTypes from "prop-types";

import { noop } from "../../utils";
import { getXValue } from "../../utils/ChartDataUtil";
import { saveNodeType } from "../utils";

import { getNewXY } from "./EachTrendLine";
import ClickableCircle from "../components/ClickableCircle";
import HoverTextNearMouse from "../components/HoverTextNearMouse";
import GenericChartComponent from "../../GenericChartComponent";
import { getMouseCanvas } from "../../GenericComponent";

const DEFAULT_FAN_LEVELS = [
  { level: 0, display: true },
  { level: 38.2, display: true },
  { level: 50, display: true },
  { level: 61.8, display: true },
  { level: 100, display: true },
];

class EachFibonacciFan extends Component {
  constructor(props) {
    super(props);

    this.handlePoint0Drag = this.handlePoint0Drag.bind(this);
    this.handlePoint1Drag = this.handlePoint1Drag.bind(this);
    this.handleLineDragStart = this.handleLineDragStart.bind(this);
    this.handleLineMove = this.handleLineMove.bind(this);
    this.handleHover = this.handleHover.bind(this);

    this.isHover = this.isHover.bind(this);
    this.saveNodeType = saveNodeType.bind(this);
    this.nodes = {};

    this.renderSVG = this.renderSVG.bind(this);
    this.drawOnCanvas = this.drawOnCanvas.bind(this);

    this.state = {
      hover: false,
    };
  }

  handleHover(moreProps) {
    if (this.state.hover !== moreProps.hovering) {
      this.setState({
        hover: moreProps.hovering,
      });
    }
  }

  handleLineDragStart() {
    const { x1, y1, x2, y2 } = this.props;
    this.dragStart = { x1, y1, x2, y2 };
  }

  handleLineMove(moreProps) {
    const { index, onDrag } = this.props;
    const {
      x1: x1Value,
      y1: y1Value,
      x2: x2Value,
      y2: y2Value,
    } = this.dragStart;

    const {
      xScale,
      chartConfig: { yScale },
      xAccessor,
      fullData,
    } = moreProps;
    const { startPos, mouseXY } = moreProps;

    const x1 = xScale(x1Value);
    const y1 = yScale(y1Value);
    const x2 = xScale(x2Value);
    const y2 = yScale(y2Value);

    const dx = startPos[0] - mouseXY[0];
    const dy = startPos[1] - mouseXY[1];

    const newX1Value = getXValue(
      xScale,
      xAccessor,
      [x1 - dx, y1 - dy],
      fullData,
    );
    const newY1Value = yScale.invert(y1 - dy);
    const newX2Value = getXValue(
      xScale,
      xAccessor,
      [x2 - dx, y2 - dy],
      fullData,
    );
    const newY2Value = yScale.invert(y2 - dy);

    onDrag(index, {
      x1: newX1Value,
      y1: newY1Value,
      x2: newX2Value,
      y2: newY2Value,
    });
  }

  handlePoint0Drag(moreProps) {
    const { index, onDrag } = this.props;
    const { x2, y2 } = this.props;

    const [x1Value, y1Value] = getNewXY(moreProps);

    onDrag(index, {
      x1: x1Value,
      y1: y1Value,
      x2,
      y2,
    });
  }

  handlePoint1Drag(moreProps) {
    const { index, onDrag } = this.props;
    const { x1, y1 } = this.props;

    const [x2Value, y2Value] = getNewXY(moreProps);

    onDrag(index, {
      x1,
      y1,
      x2: x2Value,
      y2: y2Value,
    });
  }

  helper(props, moreProps) {
    const { x1, y1, x2, y2 } = props;
    const { xScale, chartConfig } = moreProps;
    const { yScale } = chartConfig;

    // Convert data coords to pixel coords
    const px0 = xScale(x1);
    const py0 = yScale(y1);
    const px1 = xScale(x2);
    const py1 = yScale(y2);

    // Panel boundaries
    const panelLeft = chartConfig.origin[0];
    const panelRight = chartConfig.origin[0] + chartConfig.width;

    // Determine direction and far edge
    const farX = px1 > px0 ? panelRight : panelLeft;

    // Fibonacci levels to draw - configurable via appearance.levels
    const configuredLevels = props.appearance?.levels || DEFAULT_FAN_LEVELS;
    const activeFanLevels = configuredLevels.filter((l) => l.display !== false);
    const levels = activeFanLevels.map((l) => l.level / 100);

    // Calculate fan lines
    const fanLines = levels.map((level) => {
      // Calculate intersection point on imaginary vertical line at x1
      // y = pixelFromValue(p0[0], (p0[1] - p1[1]) * level + p1[1])
      const levelY = yScale((y1 - y2) * level + y2);

      // Find x-coordinate where this intersects the angled reference line
      // Since we're using a vertical line at px1, x is just px1
      const levelX = px1;

      // Project line from (px0, py0) through (levelX, levelY) to panel edge
      // farY = (farX - px0) * (levelY - py0) / (levelX - px0) + py0
      let farY;
      if (Math.abs(levelX - px0) < 0.001) {
        // Vertical line edge case
        farY = levelY;
      } else {
        farY = ((farX - px0) * (levelY - py0)) / (levelX - px0) + py0;
      }

      return {
        level,
        percent: level * 100,
        x0: px0,
        y0: py0,
        farX,
        farY,
      };
    });

    return {
      fanLines,
      px0,
      py0,
      px1,
      py1,
      panelLeft,
      panelRight,
    };
  }

  isHover(moreProps) {
    const { mouseXY } = moreProps;
    const [mx, my] = mouseXY;

    const { fanLines, px0, py0, px1, py1 } = this.helper(this.props, moreProps);

    // Check if mouse is near any fan line
    const tolerance = 10;

    // Check distance to anchor points
    const distToP0 = Math.sqrt(Math.pow(mx - px0, 2) + Math.pow(my - py0, 2));
    const distToP1 = Math.sqrt(Math.pow(mx - px1, 2) + Math.pow(my - py1, 2));

    if (distToP0 < tolerance || distToP1 < tolerance) {
      return true;
    }

    // Check distance to each fan line using point-to-line distance formula
    for (const line of fanLines) {
      const { x0, y0, farX, farY } = line;

      // Calculate line length
      const lineLength = Math.sqrt(
        Math.pow(farX - x0, 2) + Math.pow(farY - y0, 2),
      );

      if (lineLength === 0) continue;

      // Calculate perpendicular distance from mouse to line
      const distance =
        Math.abs((farY - y0) * mx - (farX - x0) * my + farX * y0 - farY * x0) /
        lineLength;

      // Check if mouse is within bounding box of line segment (with tolerance)
      const minX = Math.min(x0, farX) - tolerance;
      const maxX = Math.max(x0, farX) + tolerance;
      const minY = Math.min(y0, farY) - tolerance;
      const maxY = Math.max(y0, farY) + tolerance;

      if (
        distance < tolerance &&
        mx >= minX &&
        mx <= maxX &&
        my >= minY &&
        my <= maxY
      ) {
        return true;
      }
    }

    return false;
  }

  renderSVG(moreProps) {
    const { appearance, selected } = this.props;
    const { hover } = this.state;

    const stroke = appearance.stroke?.color || "#FF9800";
    const strokeWidth = appearance.stroke?.width || 1;
    const strokeOpacity =
      appearance.stroke?.opacity !== undefined ? appearance.stroke.opacity : 1;
    const strokeDasharray =
      appearance.stroke?.dash === "Solid" ? "none" : "5,5";

    const fontFamily =
      appearance.font?.fontFamily ||
      "Helvetica Neue, Helvetica, Arial, sans-serif";
    const fontSize = appearance.font?.fontSize || 11;
    const fontColor = appearance.font?.color || "#FF9800";

    const { fanLines, px0, py0, px1, py1, panelRight } = this.helper(
      this.props,
      moreProps,
    );

    // Label positioning
    const labelPadding = 10;
    const direction = px1 > px0 ? 1 : -1; // 1 = right, -1 = left

    return (
      <g>
        {/* Fan lines */}
        {fanLines.map((line, i) => (
          <line
            key={`fan-line-${i}`}
            x1={line.x0}
            y1={line.y0}
            x2={line.farX}
            y2={line.farY}
            stroke={stroke}
            strokeWidth={strokeWidth}
            strokeOpacity={strokeOpacity}
            strokeDasharray={strokeDasharray}
          />
        ))}

        {/* Level labels */}
        {fanLines.map((line, i) => {
          const labelX =
            direction > 0
              ? line.farX - labelPadding * 10
              : line.farX + labelPadding * 10;
          const labelY = line.farY;
          const textAnchor = direction > 0 ? "end" : "start";

          return (
            <text
              key={`fan-label-${i}`}
              x={labelX}
              y={labelY}
              fill={fontColor}
              fontSize={fontSize}
              fontFamily={fontFamily}
              textAnchor={textAnchor}
              dominantBaseline="middle"
            >
              {line.percent.toFixed(1)}%
            </text>
          );
        })}

        {/* Drag handles */}
        {(selected || hover) && (
          <g>
            <ClickableCircle
              ref={this.saveNodeType("point0")}
              show={true}
              cx={px0}
              cy={py0}
              r={6}
              fill="#FF9800"
              stroke="#FFFFFF"
              strokeWidth={2}
              strokeOpacity={1}
              interactiveCursorClass="react-stockcharts-move-cursor"
              onDrag={this.handlePoint0Drag}
              onDragComplete={this.props.onDragComplete}
            />
            <ClickableCircle
              ref={this.saveNodeType("point1")}
              show={true}
              cx={px1}
              cy={py1}
              r={6}
              fill="#FF9800"
              stroke="#FFFFFF"
              strokeWidth={2}
              strokeOpacity={1}
              interactiveCursorClass="react-stockcharts-move-cursor"
              onDrag={this.handlePoint1Drag}
              onDragComplete={this.props.onDragComplete}
            />
          </g>
        )}
      </g>
    );
  }

  drawOnCanvas(ctx, moreProps) {
    const { appearance, selected } = this.props;
    const { hover } = this.state;

    const stroke = appearance.stroke?.color || "#FF9800";
    const strokeWidth = appearance.stroke?.width || 1;
    const strokeOpacity =
      appearance.stroke?.opacity !== undefined ? appearance.stroke.opacity : 1;
    const strokeDasharray = appearance.stroke?.dash === "Solid" ? [] : [5, 5];

    const fontFamily =
      appearance.font?.fontFamily ||
      "Helvetica Neue, Helvetica, Arial, sans-serif";
    const fontSize = appearance.font?.fontSize || 11;
    const fontColor = appearance.font?.color || "#FF9800";

    const { fanLines, px0, py0, px1, py1 } = this.helper(this.props, moreProps);

    // Draw fan lines
    ctx.strokeStyle = stroke;
    ctx.lineWidth = strokeWidth;
    ctx.globalAlpha = strokeOpacity;

    if (strokeDasharray.length > 0) {
      ctx.setLineDash(strokeDasharray);
    } else {
      ctx.setLineDash([]);
    }

    fanLines.forEach((line) => {
      ctx.beginPath();
      ctx.moveTo(line.x0, line.y0);
      ctx.lineTo(line.farX, line.farY);
      ctx.stroke();
    });

    // Draw labels
    ctx.globalAlpha = 1;
    ctx.fillStyle = fontColor;
    ctx.font = `${fontSize}px ${fontFamily}`;
    ctx.textBaseline = "middle";

    const labelPadding = 10;
    const direction = px1 > px0 ? 1 : -1;

    fanLines.forEach((line) => {
      const labelX =
        direction > 0
          ? line.farX - labelPadding * 10
          : line.farX + labelPadding * 10;
      const labelY = line.farY;

      ctx.textAlign = direction > 0 ? "right" : "left";
      ctx.fillText(`${line.percent.toFixed(1)}%`, labelX, labelY);
    });

    // Draw drag handles if selected or hovered
    if (selected || hover) {
      ctx.globalAlpha = 1;
      ctx.setLineDash([]);

      // Draw point 0
      ctx.fillStyle = "#FF9800";
      ctx.strokeStyle = "#FFFFFF";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(px0, py0, 6, 0, 2 * Math.PI);
      ctx.fill();
      ctx.stroke();

      // Draw point 1
      ctx.beginPath();
      ctx.arc(px1, py1, 6, 0, 2 * Math.PI);
      ctx.fill();
      ctx.stroke();
    }

    // Reset context
    ctx.globalAlpha = 1;
    ctx.setLineDash([]);
  }

  render() {
    const { x1, y1, x2, y2, hoverText, selected } = this.props;
    const { hover } = this.state;

    if (x2 === null || y2 === null) {
      return null;
    }

    const {
      enable: hoverTextEnabled,
      selectedText: hoverTextSelected,
      text: hoverTextUnselected,
      ...restHoverTextProps
    } = hoverText;

    return (
      <g>
        <GenericChartComponent
          isHover={this.isHover}
          onHover={this.handleHover}
          onUnHover={this.handleHover}
          onDragStart={this.handleLineDragStart}
          onDrag={this.handleLineMove}
          onDragComplete={this.props.onDragComplete}
          svgDraw={this.renderSVG}
          canvasToDraw={getMouseCanvas}
          canvasDraw={this.drawOnCanvas}
          interactiveCursorClass="react-stockcharts-move-cursor"
          selected={selected}
          enableDragOnHover={true}
          drawOn={["mousemove", "pan", "drag", "xAxisZoom"]}
        />

        <HoverTextNearMouse
          show={hoverTextEnabled && hover && !selected}
          {...restHoverTextProps}
          text={hoverTextUnselected}
        />
      </g>
    );
  }
}

EachFibonacciFan.propTypes = {
  index: PropTypes.number,

  x1: PropTypes.number.isRequired,
  y1: PropTypes.number.isRequired,
  x2: PropTypes.number,
  y2: PropTypes.number,

  appearance: PropTypes.object.isRequired,
  hoverText: PropTypes.object.isRequired,

  selected: PropTypes.bool,

  onDrag: PropTypes.func.isRequired,
  onDragComplete: PropTypes.func.isRequired,
};

EachFibonacciFan.defaultProps = {
  onDrag: noop,
  onDragComplete: noop,
  selected: false,

  hoverText: {
    enable: false,
  },
};

export default EachFibonacciFan;
